/**
 * useFeatureFlag — React hook for backend-driven feature flags.
 *
 * Fetches flag value from GET /v2/callcenter/feature-flags (cached 30s).
 * Falls back to localStorage override in development mode.
 *
 * Usage:
 *   const { enabled, loading } = useFeatureFlag('ccv2_phase5_ui_enabled');
 *
 * Phase 6: replaces localStorage-only isPhase5Enabled().
 */

import { useState, useEffect, useRef } from 'react';
import { ccv2Fetch, getCcv2TenantId } from '../services/callcenterV2';

const CACHE_TTL_MS = 30_000;
const flagCache = new Map(); // key -> { value: boolean, expiresAt: number }

/**
 * Reads a feature flag value from the backend, with 30s in-memory cache.
 * In non-production, localStorage key 'ccv2_ff_override_{flagKey}' overrides backend.
 *
 * @param {string} flagKey
 * @param {boolean} defaultValue
 * @returns {{ enabled: boolean, loading: boolean, error: string|null }}
 */
function useFeatureFlag(flagKey, defaultValue = false) {
  const [enabled, setEnabled] = useState(defaultValue);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      // Dev override via localStorage (non-production only)
      if (process.env.NODE_ENV !== 'production') {
        const override = localStorage.getItem(`ccv2_ff_override_${flagKey}`);
        if (override !== null) {
          if (!cancelled && mountedRef.current) {
            setEnabled(override === 'true');
            setLoading(false);
          }
          return;
        }
      }

      // Check in-memory cache
      const cacheKey = `${getCcv2TenantId()}:${flagKey}`;
      const cached = flagCache.get(cacheKey);
      if (cached && Date.now() < cached.expiresAt) {
        if (!cancelled && mountedRef.current) {
          setEnabled(cached.value);
          setLoading(false);
        }
        return;
      }

      // Fetch from backend
      try {
        const flags = await ccv2Fetch('/v2/callcenter/feature-flags');
        const flag = Array.isArray(flags)
          ? flags.find(f => f.flagKey === flagKey || f.flag_key === flagKey)
          : flags?.[flagKey];

        const value = flag
          ? (typeof flag === 'boolean' ? flag : Boolean(flag.enabled ?? flag.value))
          : defaultValue;

        flagCache.set(cacheKey, { value, expiresAt: Date.now() + CACHE_TTL_MS });

        if (!cancelled && mountedRef.current) {
          setEnabled(value);
          setLoading(false);
          setError(null);
        }
      } catch (err) {
        if (!cancelled && mountedRef.current) {
          // Graceful fallback to default
          setEnabled(defaultValue);
          setLoading(false);
          setError(err?.message ?? 'Feature flag fetch failed');
        }
      }
    };

    void load();
    return () => { cancelled = true; };
  }, [flagKey, defaultValue]);

  return { enabled, loading, error };
}

/** Synchronous version using cache only (no fetch). */
export function getFeatureFlagCached(flagKey, defaultValue = false) {
  const tenantId = getCcv2TenantId();
  const cacheKey = `${tenantId}:${flagKey}`;
  const cached = flagCache.get(cacheKey);
  if (cached && Date.now() < cached.expiresAt) return cached.value;
  return defaultValue;
}

/** Invalidate cache for a specific flag. */
export function invalidateFeatureFlagCache(flagKey) {
  for (const key of flagCache.keys()) {
    if (key.endsWith(`:${flagKey}`)) flagCache.delete(key);
  }
}

export default useFeatureFlag;
